import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Chip, Collapse } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import GroupIcon from '@mui/icons-material/Group';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { RootState } from '../../../store';
import { GroupsTeamsService } from '../../../services/groups-teams.service';
import { TagsService } from '../../../services/tags-service';
import {
    GT_COLORS,
    StatCard,
    DenseTable,
    TagPills,
    WatchButton,
    isWatchingGroup,
    isWatchingTeam,
} from '../../../components/groups-teams/shared';
import type { DenseColumn } from '../../../components/groups-teams/shared';
import type { L2Team } from '../../../store/groups-teams.store';
import { GroupDetailsCard, TagOption } from './cards/GroupDetailsCard';
import { GroupLeadersCard, GroupLeader } from './cards/GroupLeadersCard';
import { GroupFeatureConfigCard } from './cards/GroupFeatureConfigCard';
import { CreateTeamModal } from './modals/CreateTeamModal';
import { AddGroupLeaderModal } from './modals/AddGroupLeaderModal';
import { DeleteGroupModal } from './modals/DeleteGroupModal';
import { pluralize } from '../../../helpers/pluralize';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { toast } from 'react-toastify';

export const TeamsInGroupPage: React.FC = () => {
    const navigate = useNavigate();
    const { groupId } = useParams<{ groupId: string }>();
    const { l2Group, l2Teams, l2Stats, loading, mySubscriptions } = useSelector(
        (state: RootState) => state.groupsTeams
    );

    const canManageGroups = permissionCheck(customerPermissionFlags.GROUP_LEADER);
    const canManageTeams = permissionCheck(customerPermissionFlags.TEAM_LEADER);

    // Modal state
    const [createOpen, setCreateOpen] = useState(false);
    const [addLeaderOpen, setAddLeaderOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(false);

    // Inline editing state (like L3)
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
    const [allTags, setAllTags] = useState<TagOption[]>([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [checkOnMeEnabled, setCheckOnMeEnabled] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        // Clear stale L2 data before fetching new group
        GroupsTeamsService.dispatch.setGroupDetail({
            group: null,
            teams: [],
            stats: { teams: 0, members: 0, groupLeaders: 0 },
        });
        if (groupId) GroupsTeamsService.fetchGroupDetail(groupId, (ok, msg) => {
            if (!ok) toast.error(msg || 'Failed to load group.');
        });
        GroupsTeamsService.loadMySubscriptions();
    }, [groupId]);

    // Sync store → local state when data loads
    useEffect(() => {
        if (l2Group) {
            setName(l2Group.name || '');
            setLocation(l2Group.location || '');
            setCheckOnMeEnabled(l2Group.settings?.checkOnMeEnabled);

            // Load tags and resolve group's tag IDs
            setTagsLoading(true);
            TagsService.fetchAll().then((tags) => {
                const opts: TagOption[] = (tags || []).map((t: any) => ({ id: t._id || t.id, name: t.name, color: t.color }));
                setAllTags(opts);
                const matched = (l2Group.tags || [])
                    .map((tagId: string) => opts.find((o) => o.id === tagId))
                    .filter(Boolean) as TagOption[];
                setSelectedTags(matched);
            }).catch((err) => console.error('[GroupDetail] tag fetch failed', err)).finally(() => setTagsLoading(false));
        }
    }, [l2Group]);

    // Track latest local state in refs so the save helper always reads current values
    const nameRef = useRef(name);
    nameRef.current = name;
    const locationRef = useRef(location);
    locationRef.current = location;
    const selectedTagsRef = useRef(selectedTags);
    selectedTagsRef.current = selectedTags;
    const checkOnMeRef = useRef(checkOnMeEnabled);
    checkOnMeRef.current = checkOnMeEnabled;

    /** Persist the full group, merging in any overrides. */
    const saveGroup = useCallback(async (overrides?: Record<string, any>) => {
        if (!groupId) return;
        const currentName = overrides?.name ?? nameRef.current;
        if (!currentName.trim()) return;

        // Resolve tags — create any new ones
        const tags: TagOption[] = overrides?.selectedTags ?? selectedTagsRef.current;
        const finalTagIds: string[] = [];
        try {
            for (const tag of tags) {
                if (tag.isNew) {
                    const created = await TagsService.createTag({ name: tag.name });
                    if (!created) {
                        toast.error(`Failed to create tag "${tag.name}".`);
                        return;
                    }
                    finalTagIds.push((created as any)._id || (created as any).id);
                } else {
                    finalTagIds.push(tag.id);
                }
            }
        } catch {
            toast.error('Failed to create tag. Please try again.');
            return;
        }

        GroupsTeamsService.updateGroup(
            groupId,
            {
                name: (overrides?.name ?? nameRef.current).trim(),
                location: (overrides?.location ?? locationRef.current).trim() || undefined,
                tags: finalTagIds,
                teamIds: l2Group?.teamIds || l2Teams.map((t) => t.id),
                settings: { ...l2Group?.settings, checkOnMeEnabled: overrides?.checkOnMeEnabled ?? checkOnMeRef.current },
                leadersIds: l2Group?.leadersIds || l2Group?.groupLeaders?.map((l) => l.id) || [],
            },
            (isValid, message) => {
                if (isValid) {
                    toast.success('Group updated.');
                } else {
                    toast.error(message || 'Failed to update group.');
                }
            }
        );
    }, [groupId, l2Group]);

    const handleDeleteGroup = () => {
        if (!groupId) return;
        GroupsTeamsService.deleteGroup(groupId, (isValid, message) => {
            if (isValid) {
                toast.success(`${l2Group?.name || 'Group'} deleted.`);
                navigate('/groups-teams');
            } else {
                toast.error(message || 'Failed to delete group.');
            }
        });
    };

    const handleRemoveLeader = (leaderId: string) => {
        if (!groupId || loading.saving) return;
        const currentLeaderIds = l2Group?.leadersIds || l2Group?.groupLeaders?.map((l) => l.id) || [];
        const newLeaderIds = currentLeaderIds.filter((id) => id !== leaderId);
        GroupsTeamsService.updateGroup(groupId, {
            name: l2Group?.name || name,
            location: l2Group?.location || location,
            tags: l2Group?.tags || [],
            teamIds: l2Group?.teamIds || l2Teams.map((t) => t.id),
            settings: l2Group?.settings || {},
            leadersIds: newLeaderIds,
        }, (isValid, message) => {
            if (isValid) {
                toast.success('Leader removed.');
                GroupsTeamsService.fetchGroupDetail(groupId);
            } else {
                toast.error(message || 'Failed to remove leader.');
            }
        });
    };

    const handleAddLeaderDone = () => {
        if (groupId) GroupsTeamsService.fetchGroupDetail(groupId);
    };

    const formatLeaders = (leaders: L2Team['leaders']) => {
        if (!leaders || leaders.length === 0) return <Typography sx={{ color: GT_COLORS.textDisabled, fontSize: '14px' }}>—</Typography>;
        const names = leaders.map((l) => {
            if (l.firstName && l.lastName) return `${l.firstName[0]}. ${l.lastName}`;
            if (l.fullname) return l.fullname;
            return l.email || 'Unknown';
        });
        if (names.length <= 2) return <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '14px' }}>{names.join(', ')}</Typography>;
        return (
            <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '14px' }}>
                {names.slice(0, 2).join(', ')} +{names.length - 2}
            </Typography>
        );
    };

    const comStatus = (settings: L2Team['settings']) => {
        if (settings?.checkOnMeEnabled === true) {
            return (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor: GT_COLORS.greenLight,
                        color: GT_COLORS.green,
                    }}
                >
                    Enabled
                </Box>
            );
        }
        if (settings?.checkOnMeEnabled === false) {
            return (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor: GT_COLORS.badgeGrayBg,
                        color: GT_COLORS.badgeGrayColor,
                    }}
                >
                    Disabled
                </Box>
            );
        }
        return (
            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                Org default
            </Typography>
        );
    };

    const columns: DenseColumn<L2Team>[] = [
        {
            id: 'name',
            label: 'Team Name',
            sortable: true,
            getSortValue: (r) => r.name || '',
            render: (r) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>{r.name}</strong>
                    {r.memberCount === 0 && (
                        <Chip
                            label="Needs Setup"
                            size="small"
                            sx={{
                                fontSize: '11px',
                                height: 20,
                                fontWeight: 500,
                                backgroundColor: GT_COLORS.amberLight,
                                color: GT_COLORS.amber,
                            }}
                        />
                    )}
                </Box>
            ),
        },
        {
            id: 'location',
            label: 'Location',
            render: (r) => (
                <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '14px' }}>
                    {r.location || '—'}
                </Typography>
            ),
        },
        {
            id: 'members',
            label: 'Members',
            sortable: true,
            getSortValue: (r) => r.memberCount,
            render: (r) => r.memberCount,
        },
        {
            id: 'leaders',
            label: 'Leaders',
            render: (r) => formatLeaders(r.leaders),
        },
        {
            id: 'tags',
            label: 'Tags',
            render: (r) => <TagPills tags={r.tags} max={2} />,
        },
        {
            id: 'com',
            label: 'Check On Me',
            render: (r) => comStatus(r.settings),
        },
        {
            id: 'alerts',
            label: '',
            width: 90,
            align: 'center',
            render: (r) => {
                const watching = isWatchingTeam(mySubscriptions, r.id, groupId);
                return (
                    <WatchButton
                        watching={watching}
                        onClick={() => {
                            GroupsTeamsService.toggleSubscription('team', r.id, watching, (ok) => {
                                if (ok) toast.success(watching ? 'Unsubscribed from alerts.' : 'Subscribed to alerts!');
                                else toast.error('Failed to update subscription.');
                            }, { groupId });
                        }}
                    />
                );
            },
        },
    ];

    const groupLeaders: GroupLeader[] = (l2Group?.groupLeaders || []).map((l) => ({
        id: l.id,
        fullname: l.fullname,
        firstName: l.firstName,
        lastName: l.lastName,
        email: l.email,
    }));

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Breadcrumb */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '4px', fontSize: '13px' }}>
                <Typography
                    component="a"
                    onClick={() => navigate('/groups-teams')}
                    sx={{
                        color: GT_COLORS.primary,
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontSize: '13px',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Groups & Teams
                </Typography>
                <Typography sx={{ color: GT_COLORS.textDisabled, fontSize: '11px' }}>›</Typography>
                <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '13px' }}>
                    {l2Group?.name || '...'}
                </Typography>
            </Box>

            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '14px' }}>
                <Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 600, color: GT_COLORS.textPrimary, lineHeight: 1.2 }}>
                        {l2Group?.name || '...'}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mt: '3px' }}>
                        Edit group details, manage leaders, and configure teams.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '10px' }}>
                    <WatchButton
                        watching={isWatchingGroup(mySubscriptions, groupId, l2Teams.map((t) => t.id))}
                        onClick={() => {
                            if (!groupId) return;
                            const watching = isWatchingGroup(mySubscriptions, groupId, l2Teams.map((t) => t.id));
                            GroupsTeamsService.toggleSubscription('group', groupId, watching, (ok) => {
                                if (ok) toast.success(watching ? 'Unsubscribed from alerts.' : 'Subscribed to alerts!');
                                else toast.error('Failed to update subscription.');
                            }, { teamIds: l2Teams.map((t) => t.id) });
                        }}
                        size="medium"
                    />
                    {canManageGroups && (
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setDeleteOpen(true)}
                            sx={{
                                color: GT_COLORS.red,
                                borderColor: GT_COLORS.redBorder,
                                textTransform: 'none',
                                fontSize: '14px',
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: GT_COLORS.redBg, borderColor: GT_COLORS.red },
                            }}
                        >
                            Delete Group
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Overview zone */}
            <Box sx={{ backgroundColor: GT_COLORS.pageBg, borderRadius: '10px', padding: '12px', mb: '14px' }}>
                {/* Stats */}
                <Box sx={{ display: 'flex', gap: '12px', mb: '10px' }}>
                    <StatCard value={l2Stats.teams} label="Teams" />
                    <StatCard value={l2Stats.members} label="Members" />
                    <StatCard value={l2Stats.groupLeaders} label="Group Leaders" />
                </Box>

                {/* Collapsible Group Details + Leaders + Feature Config */}
                {/* Collapsed summary strip */}
                <Collapse in={!detailsExpanded}>
                    <Box
                        sx={{
                            backgroundColor: GT_COLORS.cardBg,
                            border: `1px solid ${GT_COLORS.border}`,
                            borderLeft: `3px solid ${GT_COLORS.primary}`,
                            borderRadius: '8px',
                            padding: '12px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', minWidth: 0 }}>
                            {location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Location</Typography>
                                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textPrimary }}>{location}</Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Leaders</Typography>
                                <Typography sx={{ fontSize: '13px', color: groupLeaders.length > 0 ? GT_COLORS.textPrimary : GT_COLORS.amber }}>
                                    {groupLeaders.length > 0
                                        ? groupLeaders.map((l) => {
                                            if (l.firstName && l.lastName) return `${l.firstName} ${l.lastName}`;
                                            if (l.fullname) return l.fullname;
                                            return l.email || 'Unknown';
                                        }).join(', ')
                                        : 'None assigned'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Tags</Typography>
                                {selectedTags.length > 0 ? (
                                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                        {selectedTags.map((t) => (
                                            <Box key={t.id} sx={{ fontSize: '12px', padding: '1px 8px', borderRadius: '4px', backgroundColor: GT_COLORS.badgeTagBg, color: GT_COLORS.badgeTagColor }}>{t.name}</Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint, fontStyle: 'italic' }}>None</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>Check On Me</Typography>
                                <Typography sx={{ fontSize: '13px', color: checkOnMeEnabled ? GT_COLORS.green : GT_COLORS.textMuted }}>
                                    {checkOnMeEnabled === true ? 'Enabled' : checkOnMeEnabled === false ? 'Disabled' : 'Not set'}
                                </Typography>
                            </Box>
                        </Box>
                        {canManageGroups && (
                            <Button
                                size="small"
                                startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
                                onClick={() => setDetailsExpanded(true)}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    color: GT_COLORS.primary,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                Edit Details
                            </Button>
                        )}
                    </Box>
                </Collapse>

                {/* Expanded edit cards */}
                <Collapse in={detailsExpanded}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '16px',
                        }}
                    >
                        <GroupDetailsCard
                            name={name}
                            location={location}
                            selectedTags={selectedTags}
                            allTags={allTags}
                            tagsLoading={tagsLoading}
                            onNameChange={setName}
                            onNameBlur={() => {
                                if (name.trim() && name !== l2Group?.name) saveGroup({ name });
                            }}
                            onLocationChange={setLocation}
                            onLocationBlur={() => {
                                if (location !== (l2Group?.location || '')) saveGroup({ location });
                            }}
                            onTagsChange={(tags) => {
                                setSelectedTags(tags);
                                saveGroup({ selectedTags: tags });
                            }}
                        />
                        <GroupLeadersCard
                            leaders={groupLeaders}
                            onAddLeader={() => setAddLeaderOpen(true)}
                            onRemoveLeader={handleRemoveLeader}
                        />
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <GroupFeatureConfigCard
                                checkOnMeEnabled={checkOnMeEnabled}
                                onToggle={(enabled) => {
                                    setCheckOnMeEnabled(enabled);
                                    saveGroup({ checkOnMeEnabled: enabled });
                                }}
                            />
                        </Box>
                    </Box>
                    <Box
                        onClick={() => setDetailsExpanded(false)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            mt: '12px',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: `1px solid ${GT_COLORS.primary}44`,
                            backgroundColor: GT_COLORS.primaryLight,
                            cursor: 'pointer',
                            transition: 'all .15s',
                            '&:hover': { backgroundColor: GT_COLORS.primary, '& *': { color: '#fff !important' } },
                        }}
                    >
                        <KeyboardArrowUpIcon sx={{ fontSize: 20, color: GT_COLORS.primary, transition: 'color .15s' }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: GT_COLORS.primary, transition: 'color .15s' }}>
                            Collapse Details
                        </Typography>
                    </Box>
                </Collapse>
            </Box>

            {/* Section divider + label */}
            <Box sx={{ borderTop: `1px solid ${GT_COLORS.borderLight}`, pt: '6px', display: 'flex', alignItems: 'center', gap: '6px', mb: '10px' }}>
                <GroupIcon sx={{ fontSize: 15, color: GT_COLORS.textFaint }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.6px' }}>
                    Teams in Group
                </Typography>
            </Box>

            {/* Teams Table */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    {pluralize(l2Teams.length, 'Team')}
                </Typography>
                {canManageTeams && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        size="small"
                        sx={{
                            backgroundColor: GT_COLORS.primary,
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: l2Teams.length === 0 ? 600 : 500,
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                            ...(l2Teams.length === 0 && {
                                '@keyframes addTeamPulse': {
                                    '0%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}88` },
                                    '70%': { boxShadow: `0 0 0 18px ${GT_COLORS.primary}00` },
                                    '100%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}00` },
                                },
                                animation: 'addTeamPulse 2s ease-in-out infinite',
                            }),
                        }}
                    >
                        Add Team
                    </Button>
                )}
            </Box>
            <DenseTable
                columns={columns}
                rows={l2Teams}
                getRowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/groups-teams/${groupId}/${r.id}`)}
                pageSize={10}
                defaultSortColumn="name"
                emptyIcon={<GroupsOutlinedIcon sx={{ fontSize: 48 }} />}
                emptyMessage="No teams in this group yet. Add one to get started."
            />

            {/* Modals */}
            {l2Group && (
                <CreateTeamModal
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    groupId={l2Group.id}
                    groupName={l2Group.name}
                    onCreated={() => groupId && GroupsTeamsService.fetchGroupDetail(groupId)}
                />
            )}
            {l2Group && (
                <AddGroupLeaderModal
                    open={addLeaderOpen}
                    onClose={() => setAddLeaderOpen(false)}
                    groupId={l2Group.id}
                    groupName={l2Group.name}
                    currentLeaderIds={l2Group.leadersIds || l2Group.groupLeaders?.map((l) => l.id) || []}
                    groupSnapshot={{
                        name: l2Group.name,
                        location: l2Group.location || '',
                        tags: l2Group.tags || [],
                        teamIds: l2Group.teamIds || l2Teams.map((t) => t.id),
                        settings: l2Group.settings || {},
                    }}
                    onAdded={handleAddLeaderDone}
                />
            )}
            <DeleteGroupModal
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDeleteGroup}
                groupName={l2Group?.name || ''}
                teamCount={l2Stats.teams}
                loading={loading.saving}
            />
        </Box>
    );
};
